import React, { useContext, useEffect, useState } from "react";

import { Grid, IconButton, Tooltip } from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import DataTable from "templates/Tables/DataTable";

import { UserContext } from "App";

import { useDispatch } from "react-redux";

import { useParams, useNavigate } from "react-router-dom";

import { GetEventDetailsAPI } from "APIs/EventAPIs";

// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";

import FormField from "layouts/receipt/components/FormField";
import { Loader } from "components/Custom/Loader";

// Event pages: smaller inner padding for the cards, so the content gets more width.
const compactCardsSx = {
  "& > div": {
    padding: {
      xs: "12px",
      md: "16px",
    },
  },
};

// Hover: golden background + white text (border and icon follow the text colour)
const goldHoverSx = ({ palette }) => ({
  "&:hover": {
    backgroundColor: palette.secondary.main,
    borderColor: palette.secondary.main,
    color: palette.white.main,
    boxShadow: "none",
  },
});

// Description: same grey background and faded text as the disabled fields (title / dates),
// but it stays scrollable (MDInput sets `pointer-events: none` on disabled fields).
const descriptionSx = ({ palette }) => ({
  backgroundColor: `${palette.grey[200]} !important`,
  "& .MuiInputBase-inputMultiline": {
    overflowY: "auto !important",
    resize: "none",
    cursor: "default",
    color: palette.text.disabled,
    WebkitTextFillColor: palette.text.disabled,
  },
});

const tableSx = ({ palette }) => ({
  "& thead th > div": {
    color: palette.primary.main,
  },

  // Tablet and below (< 992px)
  "@media (max-width: 991.98px)": {
    "& th, & td": {
      paddingLeft: "8px",
      paddingRight: "8px",
    },

    "& td > div": {
      width: "auto",
    },
  },

  // Phones (< 576px)
  "@media (max-width: 575.98px)": {
    "& .MuiTableContainer-root > div:first-of-type": {
      flexWrap: "wrap",
      gap: "12px",
      padding: "16px 12px",
    },

    "& .MuiTableContainer-root > div:first-of-type > div": {
      width: "100%",
      marginLeft: 0,
      marginRight: 0,
    },
  },

  // Small phones (<= 500px)
  "@media (max-width: 500px)": {
    "& th, & td": {
      paddingLeft: "4px",
      paddingRight: "4px",
    },

    "& td .MuiButton-root": {
      width: "4.5rem",
      minWidth: 0,
      fontSize: "0.7rem",
    },
  },
});

const tileSx =
  (isNew = false) =>
  ({ palette, borders }) => ({
    position: "relative",
    display: "block",
    width: "100%",
    height: 140,
    overflow: "hidden",
    textDecoration: "none",
    borderRadius: borders.borderRadius.lg,
    backgroundColor: palette.light.main,
    border: isNew
      ? `2px solid ${palette.secondary.main}`
      : `${borders.borderWidth[1]} solid ${borders.borderColor}`,
  });

const ViewEvent = () => {
  const userData = useContext(UserContext);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { id } = useParams();

  const { accentName } = useAccent();

  const [eventData, setEventData] = useState(null);

  const [photos, setPhotos] = useState([]);
  const [isFetchingData, setIsFetchingData] = useState(true);

  const getEventFunc = async (eventId, access_token, dispatchFunc) => {
    setIsFetchingData(true);
    const response = await GetEventDetailsAPI(eventId, access_token, dispatchFunc);

    if (response !== null) {
      setEventData(response);
    }
    setIsFetchingData(false);
  };

  useEffect(() => {
    if (userData?.access_token !== null && userData?.access_token !== undefined) {
      getEventFunc(id, userData.access_token, dispatch);
    }
  }, [userData, id]);

  useEffect(() => {
    if (eventData?.media_list) {
      // Filter strictly for photos (media_type == 1)
      const photoMedia = eventData.media_list.filter((item) => item.media_type !== 2);

      setPhotos(photoMedia);
    }
  }, [eventData]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const dateOnly = dateString.slice(0, 10);

    return dateOnly.split("-").reverse().join("-");
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";

    return timeString.length >= 16 ? timeString.slice(11, 16) : timeString;
  };

  // Description text with real line breaks
  const formatDescription = (text) => {
    if (!text) return "No description provided.";

    return text.replace(/\\n/g, "\n");
  };

  return (
    <MDBox px={{ xs: 0, md: 1 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <SectionHeader
        icon="visibility"
        label="VIEW EVENT"
        startAction={
          <MDButton
            variant="outlined"
            color={accentName}
            size="medium"
            sx={[ranuOutlinedButtonSx, goldHoverSx]}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/event")}
          >
            Back
          </MDButton>
        }
        endAction={
          <MDBox
            sx={{
              display: { xs: "none", md: "block" },
            }}
          >
            <MDButton
              variant="outlined"
              color="primary"
              sx={[ranuOutlinedButtonSx, goldHoverSx]}
              startIcon={<EditIcon />}
              onClick={() => navigate(`/event/edit/${id}`)}
            >
              Edit Event
            </MDButton>
          </MDBox>
        }
      />
      {isFetchingData ? (
        <Loader />
      ) : (
        <>
          <MDBox sx={compactCardsSx}>
            {/* ------------------------------ Details ------------------------------ */}
            <SectionCard title="Details">
              <Grid container spacing={2}>
                <FormField name="title" full>
                  <MDInput disabled fullWidth value={eventData?.event_title || ""} />
                </FormField>

                <FormField name="start_date">
                  <MDInput disabled fullWidth value={formatDate(eventData?.event_start_date)} />
                </FormField>

                <FormField name="end_date">
                  <MDInput disabled fullWidth value={formatDate(eventData?.event_end_date)} />
                </FormField>

                <FormField name="description" full>
                  <MDInput
                    fullWidth
                    multiline
                    rows={6}
                    value={formatDescription(eventData?.event_description)}
                    InputProps={{ readOnly: true }}
                    sx={descriptionSx}
                  />
                </FormField>
              </Grid>
            </SectionCard>

            {/* ----------------------------- Schedule ------------------------------ */}
            <SectionCard title="Schedule" subtitle="Event schedule">
              {/* The table has its own padding, so cancel the card padding around it */}
              <MDBox
                mx={{ xs: "-12px", md: "-16px" }}
                mb={{ xs: "-12px", md: "-16px" }}
                sx={tableSx}
              >
                <DataTable
                  table={{
                    columns: [
                      {
                        Header: "Title",
                        accessor: "title",
                        align: "left",
                      },
                      {
                        Header: "Date",
                        accessor: "date",
                        align: "center",
                      },
                      {
                        Header: "Time",
                        accessor: "time",
                        align: "center",
                      },
                    ],

                    rows:
                      eventData?.schedule_list?.map((item) => ({
                        title: (
                          <MDTypography variant="body2" fontWeight="small" sx={{ color: "#000" }}>
                            {item?.event_schedule_title}
                          </MDTypography>
                        ),

                        date: (
                          <MDTypography variant="body2" fontWeight="small" sx={{ color: "#000" }}>
                            {formatDate(item?.event_schedule_date)}
                          </MDTypography>
                        ),

                        time: (
                          <MDTypography variant="body2" fontWeight="small" sx={{ color: "#000" }}>
                            {formatTime(item?.event_schedule_time)}
                          </MDTypography>
                        ),
                      })) || [],
                  }}
                  isSorted={false}
                  showTotalEntries={false}
                  noEndBorder
                  entriesPerPage={{
                    defaultValue: 10,
                    entries: [10, 25, 50],
                  }}
                />
              </MDBox>
            </SectionCard>

            {/* ------------------------------- Media ------------------------------- */}
            <SectionCard
              title={`Media (${photos.length})`}
              subtitle="Click any image to open it in a new tab"
            >
              <Grid container spacing={2}>
                {photos.length > 0 ? (
                  photos.map((item, index) => (
                    <Grid item xs={6} sm={4} md={3} key={item.event_media_id || index}>
                      <MDBox
                        component="a"
                        href={item.media}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={tileSx(false)}
                      >
                        <img
                          src={item.media}
                          alt={`event_media_${index}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />

                        <Tooltip title="Open image in new tab">
                          <IconButton
                            size="small"
                            style={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              backgroundColor: "rgba(0, 0, 0, 0.65)",
                              color: "#ffffff",
                              padding: "4px",
                            }}
                          >
                            <OpenInNewIcon style={{ fontSize: "16px" }} />
                          </IconButton>
                        </Tooltip>
                      </MDBox>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <MDTypography variant="button" color="text">
                      No photos uploaded for this event.
                    </MDTypography>
                  </Grid>
                )}
              </Grid>
            </SectionCard>
          </MDBox>

          {/* Mobile Edit Button */}
          <MDBox
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              mt: 2,
            }}
          >
            <MDButton
              color="primary"
              size="medium"
              sx={[ranuOutlinedButtonSx, goldHoverSx]}
              startIcon={<EditIcon />}
              onClick={() => navigate(`/blog/edit/${id}`)}
            >
              Edit Event
            </MDButton>
          </MDBox>
        </>
      )}
    </MDBox>
  );
};

export default ViewEvent;
